"use client";

import React, { useState } from "react";
import Image from "next/image";
import BrochurePopupForm from "@/components/newPageComponent/CommonSection/BrochurePopupForm";

interface ExamPoint {
  icon: string;
  label: string;
}

interface ExamCard {
  id: "fin" | "strat";
  image: string;
  title: string;
  points: ExamPoint[];
}

const examCards: ExamCard[] = [
  {
    id: "fin",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/new-cma-part-1.webp",
    title: "Financial Planning, Performance & Analytics",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon1.webp",
        label: "Driver-based rolling forecast agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon2.webp",
        label: "Scenario & sensitivity modeling",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon3.webp",
        label: "Flexible budget variance agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon4.webp",
        label: "Balanced scorecard & strategy maps",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon5.webp",
        label: "ABC costing allocation engines",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon6.webp",
        label: "SOX control testing workflows",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon7.webp",
        label: "Fraud anomaly detection dashboards",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part1_icon8.webp",
        label: "Predictive AR collections scoring",
      },
    ],
  },
  {
    id: "strat",
    image:
      "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/new-cma-part-2.webp",
    title: "Strategic Financial Management",
    points: [
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon1.webp",
        label: "Ratio & peer benchmark dashboards",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon2.webp",
        label: "WACC & cost-of-capital agents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon3.webp",
        label: "Working capital release modeling",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon4.webp",
        label: "Make-or-buy decision engines",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon5.webp",
        label: "CVP & breakeven modeling",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon6.webp",
        label: "NPV / IRR capital project ranking",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon7.webp",
        label: "Enterprise risk register\nagents",
      },
      {
        icon: "https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/part2_icon8.webp",
        label: "IMA ethics decision advisor",
      },
    ],
  },
];

const CmaEquivalentNew = () => {
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);

  const openFormPopup = () => {
    sessionStorage.setItem("modal-open", "true");
    setFormPopupOpen(true);
  };

  const closeFormPopup = () => {
    sessionStorage.removeItem("modal-open");
    setFormPopupOpen(false);
  };

  return (
    <>
      <section className="bg-white py-8 px-4 max-md:bg-[#f3f3f3] max-md:p-4 max-md:pt-0 max-md:pb-0">
        <div className="main_container py-5 bg-white rounded-2xl">
          <div className="flex flex-col items-center text-center">
            <h2 className="m-0 text-[#333] text-[32px] font-bold leading-[1.2] tracking-[-0.02em] max-md:text-[22px] max-md:leading-[1.15] [&>span]:text-[#3399ff]">
              <span>U.S. CMA =</span> Certified Management Accountant
            </h2>
            <p className="mt-8 text-[#999] text-sm font-semibold leading-none max-md:text-xs max-md:mt-2.5 max-md:px-4">
              2 Exams | 8 Months | Integrated with
              <br className="md:hidden block" /> CAIRA - AI workflows &amp;
              agents
            </p>

            <div className="flex flex-row justify-center gap-4 w-full mt-8 max-md:justify-start max-md:overflow-x-auto max-md:gap-3 max-md:mt-5 max-md:px-4 max-md:pb-1 max-md:[scrollbar-width:none] max-md:snap-x max-md:snap-mandatory max-lg:gap-3">
              {examCards.map((card) => (
                <div
                  key={card.id}
                  className="md:w-[375px] md:flex-none min-w-0 flex flex-col border border-[#DEE7F5] rounded-[10px] bg-white p-2.5 max-md:shrink-0 max-md:snap-start max-md:w-[270px]"
                >
                  <div className="relative w-full overflow-hidden rounded-lg">
                    <Image
                      src={card.image}
                      alt={card.title}
                      width={620}
                      height={300}
                      className="block w-full h-auto"
                      loading="lazy"
                    />
                    <span className="absolute bottom-2 right-2 text-[10px] font-bold text-white flex items-center">
                      Integrated with
                      <Image
                        src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/starIcon.webp"
                        alt="star icon"
                        width={12}
                        height={12}
                        loading="lazy"
                        className="mx-1 invert brightness-0"
                      />
                      CAIRA
                    </span>
                  </div>
                  <div className="pt-3 flex flex-col gap-3 flex-1 min-w-0">
                    <h3 className="m-0 text-[#0b2a4d] text-base font-bold leading-[1.2] text-center max-md:text-[15px]">
                      {card.title}
                    </h3>
                    <div className="grid grid-cols-6 gap-2 w-full max-md:grid-cols-2">
                      {card.points.map((point, idx) => {
                        const total = card.points.length;
                        const remainder = total % 3;
                        const isLastRow =
                          remainder !== 0 && idx >= total - remainder;
                        const desktopSpan = isLastRow
                          ? remainder === 1
                            ? "col-span-6"
                            : "col-span-3"
                          : "col-span-2";
                        return (
                          <div
                            key={point.label}
                            className={`${desktopSpan} min-w-0 max-md:!col-span-1 flex flex-col items-center justify-start gap-2 border border-[#E1F3F8] rounded-[8px] bg-white p-[5px] text-center`}
                          >
                            <Image
                              src={point.icon}
                              alt={point.label}
                              width={32}
                              height={32}
                              className="h-6 w-6 object-contain"
                            />
                            <p
                              className={`text-[11px] md:text-xs leading-tight font-medium text-[#1D1D1D] break-words mx-auto ${
                                isLastRow ? "" : "md:max-w-[12ch]"
                              }`}
                            >
                              {point.label
                                .split("\n")
                                .map((line, lineIdx, arr) => (
                                  <React.Fragment key={lineIdx}>
                                    {line}
                                    {lineIdx < arr.length - 1 && (
                                      <br className="hidden md:inline" />
                                    )}
                                    {lineIdx < arr.length - 1 && (
                                      <span className="md:hidden"> </span>
                                    )}
                                  </React.Fragment>
                                ))}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="mt-8 !min-w-[306px] px-[28px] py-[12px] border-0 rounded-tl-[10.85px] rounded-tr-[10.85px] rounded-br-[10.85px] rounded-bl-none bg-[#2a85ff] text-white text-[16px] font-bold leading-none cursor-pointer max-md:w-[calc(100%-32px)] max-md:min-w-0 max-md:px-4 max-md:text-[15px]"
              onClick={openFormPopup}
            >
              Download Brochure
            </button>
          </div>
        </div>
      </section>

      {isFormPopupOpen && (
        <BrochurePopupForm handleClose={closeFormPopup} pageName="CMA" />
      )}
    </>
  );
};

export default CmaEquivalentNew;
